import React, { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { HeartIcon, InfinityIcon, XIcon } from "lucide-react";
import type { AnswerState } from "../../types/exercises";
import type { LessonPhase } from "../../hooks/useLessonRunner";
import { IconButton } from "../ui/IconButton";
import { ProgressBar } from "../ui/ProgressBar";
import { Modal } from "../ui/Modal";
import { BrutalButton } from "../ui/BrutalButton";
import { CheckBar } from "./CheckBar";
import { ThemedBackdrop } from "../learn/ThemedBackdrop";
import { spring } from "../../utils/motion";
interface LessonShellProps {
  stepKey: string;
  heading: ReactNode;
  children: ReactNode;
  progress: number;
  hearts: number;
  unlimited: boolean;
  phase: LessonPhase;
  answer: AnswerState;
  skipped: boolean;
  exitTo: string;
  onCheck: () => void;
  onSkip: () => void;
  onContinue: () => void;
  onRefill: () => void;
  /** Target language code — tints the backdrop with the course's theme. */
  target?: string;
}
export function LessonShell({
  stepKey,
  heading,
  children,
  progress,
  hearts,
  unlimited,
  phase,
  answer,
  skipped,
  exitTo,
  onCheck,
  onSkip,
  onContinue,
  onRefill,
  target
}: LessonShellProps) {
  const navigate = useNavigate();
  const reduce = useReducedMotion() ?? false;
  const [confirmExit, setConfirmExit] = useState(false);
  const outOfHearts = !unlimited && hearts === 0 && phase === "checked";
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || confirmExit || outOfHearts) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "BUTTON" || tag === "A") return;
      if (phase === "checked") onContinue();else if (answer.ready) onCheck();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, answer.ready, onCheck, onContinue, confirmExit, outOfHearts]);
  return <div className={`${target ? "" : "pat-plus"} relative flex min-h-screen w-full flex-col bg-paper font-sans text-ink`} data-id="element-343" data-mp="25cet29n5f6xj">
      {target && <ThemedBackdrop target={target} mode="quiet" withRail={false} data-id="element-344" data-mp="2f4u9h2isrngb" />}
      <header className="relative z-10 mx-auto flex w-full max-w-3xl items-center gap-4 px-4 pt-5" data-id="element-345" data-mp="2f65dn3g5mx7y">
        <IconButton label="Exit lesson" onClick={() => setConfirmExit(true)} data-id="element-346" data-mp="2abrbmi6zlrub">
          <XIcon className="h-5 w-5" strokeWidth={2.5} data-id="element-347" data-mp="2o35y4z03sb64" />
        </IconButton>
        <ProgressBar value={progress} tone="good" size="lg" className="flex-1" label="Lesson progress" data-id="element-348" data-mp="2j54di9n143sj" />
        <span className="flex items-center gap-1.5 rounded-xl border-3 border-line bg-surface px-2.5 py-1.5 text-sm font-bold" aria-label={unlimited ? "Unlimited hearts" : `${hearts} hearts left`} data-id="element-349" data-mp="2y1vdak0m7hio">
          <HeartIcon className="h-4 w-4 fill-bad text-bad" aria-hidden="true" data-id="element-350" data-mp="13r9pmqvmbjeq" />
          {unlimited ? <InfinityIcon className="h-4 w-4" aria-hidden="true" data-id="element-351" data-mp="ysf8dtdau2wq" /> : <AnimatePresence mode="popLayout" initial={false} data-id="element-352" data-mp="3ae7iyt5a93rm">
              <motion.span key={hearts} initial={reduce ? {
            opacity: 0
          } : {
            y: -10,
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} exit={reduce ? {
            opacity: 0
          } : {
            y: 10,
            opacity: 0
          }} transition={spring.snappy} data-id="element-353" data-mp="3fbh9kc4erlh1">
                {hearts}
              </motion.span>
            </AnimatePresence>}
        </span>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-3xl flex-1 px-4 pb-48 pt-8 md:pt-12" data-id="element-354" data-mp="2vpupq3xkxz0t">
        {/* Enter-only: waiting for the previous exercise's exit (e.g. Fill-the-gap's shared-layout chips)
            could stall and leave the stage empty. */}
        <motion.div key={stepKey} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: 28
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={spring.smooth} data-id="element-355" data-mp="fx43u64t0e4q">
          <div className="mb-6 flex flex-wrap items-center gap-3" data-id="element-356" data-mp="2gzv7brw0a4lz">{heading}</div>
          {children}
        </motion.div>
      </main>

      <CheckBar phase={phase} answer={answer} skipped={skipped} onCheck={onCheck} onSkip={onSkip} onContinue={onContinue} data-id="element-357" data-mp="2c1bhhgeak8oc" />

      <Modal open={confirmExit} onClose={() => setConfirmExit(false)} title="Leave this lesson?" footer={<>
            <BrutalButton variant="surface" onClick={() => navigate(exitTo)} data-id="element-359" data-mp="1y1iv1x9ssem6">
              Leave
            </BrutalButton>
            <BrutalButton onClick={() => setConfirmExit(false)} data-id="element-360" data-mp="1t7xtjv8zx2i8">Keep going</BrutalButton>
          </>} data-id="element-358" data-mp="280rjkalw9115">
        You're doing well. If you leave now, progress in this lesson won't be saved.
      </Modal>

      <Modal open={outOfHearts} onClose={onRefill} title="Out of hearts" footer={<>
            <BrutalButton variant="surface" onClick={onRefill} data-id="element-362" data-mp="1aawmm4vvssbn">
              Refill and continue
            </BrutalButton>
            <BrutalButton to="/app/profile/subscription" variant="brand" data-id="element-363" data-mp="3lxqhlzqrts0x">
              Get unlimited with Plus
            </BrutalButton>
          </>} data-id="element-361" data-mp="1k7j9t0bojvgu">
        Mistakes are how you learn. On the free plan you get 5 hearts per lesson — refill for this prototype, or go unlimited with Plus.
      </Modal>
    </div>;
}